import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import Button from "../components/ui/button/Button";
import { 
  Plus, ChevronRight, User, ShieldCheck, Mail, CheckCircle2, XCircle, UserCircle
} from "lucide-react";
import axiosClient from "../api/axiosClient";

export default function UsersList({ isDark, activeFilters, onDataLoaded }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Design tokens using Slate 300 (Dark) and Slate 600 (Light)
  const secondaryTextColor = isDark ? 'text-slate-300' : 'text-slate-600';
  const brandBlue = isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]';

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axiosClient.get('/users.php');
      const formattedData = response.data.map(u => ({
        ...u,
        name: u.full_name,
        status_label: parseInt(u.is_active) === 1 ? "Active" : "Inactive"
      }));
      setUsers(formattedData);
      
      if (onDataLoaded) onDataLoaded(formattedData);
    } catch (err) {
      console.error("Failed to load users:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const globalSearchValue = activeFilters?.global_search?.[0]?.toLowerCase() || "";
      const matchesGlobal = !globalSearchValue || [
        user.full_name,
        user.email,
        user.role,
        user.status_label
      ].some(val => String(val || "").toLowerCase().includes(globalSearchValue));

      const activeStatusFilters = activeFilters?.is_active || activeFilters?.status || [];
      const matchesStatus = activeStatusFilters.length === 0 || 
                           activeStatusFilters.includes(user.status_label);

      const activeRoleFilters = activeFilters?.role || [];
      const matchesRole = activeRoleFilters.length === 0 || 
                         activeRoleFilters.includes(user.role);

      return matchesGlobal && matchesStatus && matchesRole;
    }).sort((a, b) => {
      // Sort by active status (Active first, Inactive/Closed equivalent last)
      if (a.is_active !== b.is_active) {
        return b.is_active - a.is_active; 
      }
      // Then alphabetical
      return a.full_name.localeCompare(b.full_name);
    });
  }, [users, activeFilters]);

  const getStatusConfig = (isActive) => {
    const tagBase = "px-3 py-1.5 rounded-lg text-[11px] font-bold font-brand-heading border flex items-center gap-1.5 transition-colors w-fit";
    if (parseInt(isActive) === 1) {
      return {
        label: "Active",
        icon: <CheckCircle2 size={12} />,
classes: `${tagBase} ${isDark 
  ? 'bg-[#5A9AFF]/10 text-[#5A9AFF] border-[#5A9AFF]/20' 
  : 'bg-[#2D4D94]/5 text-[#2D4D94] border-[#2D4D94]/10'}`      };
    }
    // Inactive users now use the Orange variant to match "Closed" logic
    return {
      label: "Inactive",
      icon: <XCircle size={12} />,
      classes: `${tagBase} ${isDark ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' : 'bg-orange-50 text-orange-600 border-orange-100'}`
    };
  };

  return (
    <div className={`p-1 sm:p-6 space-y-6 min-h-screen transition-colors duration-300 ${isDark ? 'bg-slate-800/0' : 'bg-slate-100/0'}`}>
      
      {/* HEADER CONTAINER */}
      <div className="relative flex flex-col items-center justify-center min-h-[120px] sm:min-h-[100px] sm:-mt-8 max-w-7xl mx-auto w-full px-4">
        <div className="text-center">
          <h2 className={`text-2xl font-brand-heading font-bold uppercase tracking-tighter leading-tight ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
            <span className="text-orange-600">Staff</span> <span className={brandBlue}> Directory</span>
          </h2>
          
          <div className="flex items-center justify-center gap-3 mt-0">
            <div className={`h-px w-4 ${isDark ? 'bg-slate-400' : 'bg-slate-400'}`} />
            <p className={`text-[13px] uppercase tracking-[0.1em] ${secondaryTextColor}`}>
              Team Members & Permissions
            </p>
            <div className={`h-px w-4 ${isDark ? 'bg-slate-400' : 'bg-slate-400'}`} />
          </div>
        </div>
        
        <div className="mt-4 sm:mt-0 sm:absolute sm:right-4 sm:top-1/2 sm:-translate-y-1/2 flex items-center">
          <Button 
            isDark={isDark}
            onClick={() => navigate('/users/new')} 
            className={`
              group flex items-center px-5 py-2.5 rounded-xl font-bold uppercase tracking-wider text-xs transition-all border shadow-sm hover:shadow-md active:scale-95 whitespace-nowrap
              ${isDark ? 'bg-slate-900/50 border-slate-700 hover:bg-slate-800' : 'bg-white border-slate-200 hover:bg-slate-50'}
            `}
          >
            <Plus size={18} strokeWidth={3} className="text-orange-600 transition-transform duration-500 ease-out group-hover:rotate-90" /> 
            <span className={`ml-2 ${brandBlue}`}>New User</span>
          </Button>
        </div>
      </div>

      {/* TABLE CONTAINER */}
      <div className={`rounded-2xl border shadow-sm overflow-hidden max-w-7xl mx-auto w-full transition-all ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
        <div className="overflow-x-auto w-full">
          <Table className="min-w-[800px] w-full text-left">
            <TableHeader className={isDark ? 'bg-slate-950/50' : 'bg-slate-50/50'}>
              <TableRow className={isDark ? 'border-slate-800' : 'border-slate-100'}>
                <TableCell isHeader className={`px-6 py-4 text-[11px] font-black font-brand-heading uppercase tracking-widest ${secondaryTextColor}`}>User</TableCell>
                <TableCell isHeader className={`px-6 py-4 text-[11px] font-black font-brand-heading uppercase tracking-widest ${secondaryTextColor}`}>Access Level</TableCell>
                <TableCell isHeader className={`px-6 py-4 text-[11px] font-black font-brand-heading uppercase tracking-widest ${secondaryTextColor}`}>Status</TableCell>
                <TableCell isHeader className="px-6 py-4 text-right"></TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="py-24 text-center">
                    <div className="flex flex-col items-center gap-3">
                       <div className={`w-8 h-8 border-2 border-t-orange-600 rounded-full animate-spin ${isDark ? 'border-slate-700' : 'border-slate-200'}`} />
                       <span className={`uppercase text-[10px] font-bold tracking-[0.2em] ${secondaryTextColor}`}>Syncing Directory...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length > 0 ? filteredUsers.map((user) => {
                const status = getStatusConfig(user.is_active);
                return (
                  <TableRow 
                    key={user.id} 
                    className={`cursor-pointer transition-all group border-b last:border-none ${isDark ? 'hover:bg-slate-800 border-slate-800' : 'hover:bg-blue-50/30 border-slate-50'}`}
                    onClick={() => navigate(`/users/${user.id}`)}
                  >
                    <TableCell className="px-6 py-5">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center border transition-colors shrink-0 ${isDark ? 'bg-slate-800 border-slate-700 text-slate-400 group-hover:border-[#5A9AFF]/50' : 'bg-slate-50 border-slate-200 text-slate-400 group-hover:border-[#2D4D94]/30'}`}>
                          <User size={18} className={`transition-colors group-hover:${brandBlue}`} />
                        </div>
                        <div>
                          <div className={`font-brand-body-bold text-sm ${isDark ? 'text-slate-100 group-hover:text-[#5A9AFF]' : 'text-slate-900 group-hover:text-[#2D4D94]'}`}>
                            {user.full_name}
                          </div>
                          <div className={`text-[12px] font-brand-heading tracking-widest flex items-center gap-1 ${secondaryTextColor}`}>
                            <Mail size={10} className={isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}/> {user.email}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-5">
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1.5 rounded-lg text-[11px] font-bold font-brand-heading uppercase border flex items-center gap-1.5 transition-colors w-fit ${isDark ? 'bg-slate-800 text-slate-300 border-slate-700' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                          <ShieldCheck size={12} className={isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'} /> {user.role}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-5 uppercase">
                      <div className={status.classes}>
                        {status.icon} {status.label}
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-5 text-right">
                      <div className={`flex justify-end items-center group-hover:translate-x-1 transition-all ${isDark ? 'text-slate-700 group-hover:text-[#5A9AFF]' : 'text-slate-300 group-hover:text-[#2D4D94]'}`}>
                        <ChevronRight size={20} />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              }) : (
                <TableRow>
                  <TableCell colSpan={4} className="py-24 text-center">
                    <div className="flex flex-col items-center gap-2 opacity-40">
                      <UserCircle size={40} className={isDark ? 'text-slate-500' : 'text-slate-400'} />
                      <p className={`text-[10px] font-brand-heading uppercase tracking-widest ${secondaryTextColor}`}>No staff members found</p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}